import {
  filterCondition,
  filterSearch,
  orFilter,
} from "@west-tennessee-consulting/supabase/postgrest-filter";
import { getSupabase } from "@/lib/hooks/api/utils";
import type {
  BulkActionProgressItem,
  BulkActionProgress,
  FilterBasedBulkActionParams,
} from "@/lib/hooks/api/shared/bulk-filter-types";

export type { BulkActionProgressItem, BulkActionProgress, FilterBasedBulkActionParams };

export interface TicketFilterCriteria {
  status?: string;
  priority?: string;
  assignedTo?: string;
  clientId?: string;
  category?: string;
  search?: string;
  exclude_id?: string;
}

export function buildTicketFilterQuery(supabase: ReturnType<typeof getSupabase>, filterCriteria: TicketFilterCriteria) {
  let query = supabase.from("tickets").select(`
    *,
    contacts!inner(
      id,
      name,
      company_name,
      email
    )
  `);

  if (filterCriteria.status) {
    query = query.eq("status", filterCriteria.status);
  }
  if (filterCriteria.priority) {
    query = query.eq("priority", filterCriteria.priority);
  }
  if (filterCriteria.assignedTo) {
    query = query.eq("assigned_to", filterCriteria.assignedTo);
  }
  if (filterCriteria.clientId) {
    query = query.eq("contact_id", filterCriteria.clientId);
  }
  if (filterCriteria.category) {
    query = query.eq("category", filterCriteria.category);
  }
  if (filterCriteria.exclude_id) {
    query = query.neq("id", filterCriteria.exclude_id);
  }

  if (filterCriteria.search) {
    const isNumber = !isNaN(Number(filterCriteria.search));
    const searchFilter = orFilter([
      filterSearch("title", filterCriteria.search),
      filterSearch("description", filterCriteria.search),
      isNumber ? filterCondition("ticket_number", "eq", filterCriteria.search.trim()) : null,
    ]);
    if (searchFilter) query = query.or(searchFilter);
  }

  return query.order("created_at", { ascending: false });
}

export async function getFilteredTicketIds(
  filterCriteria: TicketFilterCriteria
): Promise<Array<{ id: string; title: string; ticket_number?: string }>> {
  const supabase = getSupabase();
  const query = buildTicketFilterQuery(supabase, filterCriteria);

  const { data, error } = await query.select("id, title, ticket_number");

  if (error) {
    console.error("Error fetching filtered ticket IDs:", error);
    throw new Error(error.message);
  }

  return data || [];
}

export function useTicketFilterCriteria(filters: {
  status?: string;
  priority?: string;
  assignedTo?: string;
  clientId?: string;
  search?: string;
}): TicketFilterCriteria {
  return {
    status: filters.status || undefined,
    priority: filters.priority || undefined,
    assignedTo: filters.assignedTo || undefined,
    clientId: filters.clientId || undefined,
    search: filters.search || undefined,
  };
}
